<script lang="ts">
import KlereoIcon from "@/shared/components/KlereoIcon.vue";
import { defineComponent } from "vue";
import { getProbeLabel, getProbeType } from "@/shared/utils/probes";
import { injectProbe } from "@/shared/utils/injection";
import KlereoChart, { ChartPoint } from "@/features/chart/KlereoChart.vue";
import PromiseWrapper from "@/shared/components/PromiseWrapper.vue";
import { Nullable } from "@/models/nullable.type";
import { subDays, subMonths, subWeeks } from "date-fns";
import KlereoCheckbox from "@/shared/components/KlereoCheckbox.vue";
import ChartRangeSelector from "@/features/probe/history/ChartRangeSelector.vue";

export default defineComponent({
  ...injectProbe,
  components: {
    ChartRangeSelector,
    KlereoCheckbox,
    PromiseWrapper,
    KlereoChart,
    KlereoIcon,
  },
  data() {
    return {
      points: {} as Record<string, Nullable<ChartPoint[]>>,
      promise: null as Nullable<Promise<unknown>>,
      period: "lastDay" as "lastDay" | "lastWeek" | "lastMonth" | "custom",
      offset: 0 as number,
      showFiltrationOff: true,
      range: [new Date(), new Date()] as [Date, Date],
    };
  },
  computed: {
    label() {
      return getProbeLabel(this.probe.index);
    },
    probeType() {
      return getProbeType(this.probe.type);
    },
    hasData() {
      return this.points?.["_P" + this.probe.index]?.length;
    },
  },
  watch: {
    range() {
      console.log("range updated, fetching");
      this.fetch(this.range[0], this.range[1]);
    },
    offset() {
      this.updateRange();
    },
    period() {
      this.updateRange();
    },
  },
  methods: {
    fetch(from: Date, to: Date) {
      this.promise = this.api
        .getHisto({
          poolID: this.pool.idSystem,
          probeID1: this.probe.index,
          from: from.getTime() / 1000,
          to: to.getTime() / 1000,
          paramName1: "ConsigneEau",
        })
        .then((data) => {
          this.points = data;
        });
    },
    updateRange() {
      switch (this.period) {
        case "lastDay":
          this.range = [
            subDays(new Date(), 1 + this.offset),
            subDays(new Date(), this.offset),
          ];
          break;

        case "lastWeek":
          this.range = [
            subWeeks(new Date(), 1 + this.offset),
            subWeeks(new Date(), this.offset),
          ];
          break;
        case "lastMonth":
          this.range = [
            subMonths(new Date(), 1 + this.offset),
            subMonths(new Date(), this.offset),
          ];
          break;
      }
    },
  },
  created() {
    this.fetch(this.range[0], this.range[1]);
  },
});
</script>

<template>
  <div class="probe-history-view">
    <div class="flex text-white justify-between mb-5">
      <klereo-icon
        name="arrow-back"
        class="cursor-pointer w-[27px]"
        @click="$router.push(`/pool/${$route.params.poolId}`)"
      />
      {{ $t(label) }}
      <klereo-icon
        name="cog"
        class="cursor-pointer w-[22px]"
        @click="
          $router.push(
            `/pool/${$route.params.poolId}/probe/${$route.params.probeIdx}/`
          )
        "
      />
    </div>

    <div class="card p-3">
      <div class="flex">
        <div class="font-medium text-lg grow">{{ $t("Histo") }}</div>
        <chart-range-selector
          :period="{ type: period, dateRange: range }"
          @change="
            offset = 0;
            period = $event.type;
            $event.type === 'custom' && $event.dateRange
              ? (range = $event.dateRange)
              : '';
          "
        ></chart-range-selector>
      </div>

      <div class="mt-5 h-[200px] flex items-center justify-center">
        <promise-wrapper :promise="promise">
          <klereo-chart
            v-if="hasData"
            :key="showFiltrationOff.toString()"
            :min-extent="probeType.chartMinExtent"
            :min-origin="probeType.chartMinOrigin"
            :points="points['_P' + probe.index]!"
            :show-filtration-off="showFiltrationOff"
          ></klereo-chart>
          <div v-else>{{ $t("Nodata") }}</div>
        </promise-wrapper>
      </div>

      <div class="flex mt-5 flex justify-center" v-if="period !== 'custom'">
        <div class="btn--primary cursor-pointer" @click="offset++">
          <klereo-icon name="arrow-left" class="inline-block" />
          {{ $t("ChartPreviousPeriod") }}
        </div>
        <div
          class="btn--primary ml-3 cursor-pointer"
          :class="{ disabled: offset === 0 }"
          @click="offset--"
        >
          {{ $t("ChartNextPeriod") }}
          <klereo-icon name="arrow-right" class="inline-block" />
        </div>
      </div>

      <div class="text-right text-sm mt-2">
        <label>
          <klereo-checkbox v-model="showFiltrationOff" />
          {{ $t("MesHorsF") }}
        </label>
      </div>
    </div>
  </div>
</template>
